import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Chip, SearchField, Spinner, toast } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils';
import {
    FOCUS_SEARCH_SHORTCUT,
    ShortcutHint,
    toAriaKeyShortcuts,
} from '@/components/shortcuts';
import { ProductGrid } from './ProductGrid';
import { CategoryFilter } from './CategoryFilter';
import { VariantPickerModal } from './VariantPickerModal';
import { ProductPanelNotice } from './ProductPanelNotice';
import { useProductPanel } from './useProductPanel';

/**
 * Left pane: a filter bar (product search + multi-select category filter) over
 * the product grid, plus the variant picker. Render-only: `useProductPanel`
 * owns search as home, `F2`, the `Esc` layering, `↓` into the grid, the
 * printable-key redirect and where focus goes when an overlay closes; this
 * component translates and toasts the notice key search `Enter` returns.
 */
export const ProductPanel: React.FC = () => {
    const { t } = useTranslation('pages');
    const panel = useProductPanel();

    return (
        <div
            className="flex flex-1 flex-col overflow-hidden"
            {...getNodeTestIdProp('seller-products')}
        >
            <div className="flex flex-shrink-0 flex-col gap-2.5 border-b border-border px-5 py-3">
                <SearchField
                    aria-label={t('seller.searchPlaceholder')}
                    fullWidth
                    onChange={panel.setSearch}
                    value={panel.search}
                    variant="secondary"
                >
                    <SearchField.Group>
                        <SearchField.SearchIcon />
                        <SearchField.Input
                            aria-keyshortcuts={toAriaKeyShortcuts(
                                FOCUS_SEARCH_SHORTCUT.combination,
                            )}
                            className="w-full"
                            onKeyDown={(event) => {
                                const noticeKey = panel.onSearchKeyDown(event);
                                if (noticeKey) {
                                    toast.danger(t(noticeKey));
                                }
                            }}
                            placeholder={t('seller.searchPlaceholder')}
                            ref={panel.searchInputRef}
                        />
                        <SearchField.ClearButton />
                        <ShortcutHint
                            className="mr-2"
                            shortcut={FOCUS_SEARCH_SHORTCUT}
                        />
                    </SearchField.Group>
                </SearchField>
                <CategoryFilter onClose={panel.goHome} />
                {panel.productsTruncated ? (
                    <Chip
                        className="self-start"
                        color="warning"
                        size="sm"
                        variant="soft"
                        {...getNodeTestIdProp('seller-products-truncated')}
                    >
                        {t('seller.catalogTruncated')}
                    </Chip>
                ) : null}
            </div>

            <div className="flex flex-1 flex-col overflow-hidden p-4">
                {panel.productsLoading ? (
                    <div
                        className="flex h-full items-center justify-center"
                        {...getNodeTestIdProp('seller-products-loading')}
                    >
                        <Spinner aria-label={t('seller.loadingProducts')} />
                    </div>
                ) : panel.productsError ? (
                    <div
                        className="flex h-full items-center justify-center"
                        {...getNodeTestIdProp('seller-products-error')}
                    >
                        <ProductPanelNotice
                            icon="solar:danger-triangle-bold-duotone"
                            title={t('seller.productsError')}
                        />
                    </div>
                ) : panel.filteredProducts.length === 0 ? (
                    <div
                        className="flex h-full items-center justify-center"
                        {...getNodeTestIdProp('seller-products-empty')}
                    >
                        <ProductPanelNotice
                            icon="solar:box-minimalistic-bold-duotone"
                            title={t('seller.noProducts')}
                        />
                    </div>
                ) : (
                    <ProductGrid
                        inCartQty={panel.inCartQty}
                        onPrintableKey={panel.onPrintableKey}
                        onProductAddedByPointer={panel.goHome}
                        onSelectProduct={panel.selectProduct}
                        products={panel.filteredProducts}
                        ref={panel.productGridRef}
                    />
                )}
            </div>

            <VariantPickerModal
                onChoose={panel.chooseVariant}
                onClose={panel.closeVariantPicker}
                picker={panel.variantPicker}
            />
        </div>
    );
};
